import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { TrashIcon, PencilIcon } from '@phosphor-icons/react';
import { supabase } from '@/lib/supabase';
import { SettingsContent } from '@/content/SettingsContent';
import { useNotificationPreferences } from '@/lib/hooks/useNotificationPreferences';
import {
  requestNotificationPermission,
  isPushEnabled,
} from '@/lib/services/onesignal';
import { NotificationSettings } from '@/components/NotificationSettings';
import { clearAllCaches } from '@/lib/services/cacheManager';
import type { TestNotificationState } from '@/lib/types/notifications.types';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Banner } from '@/components/Banner';
import { ActionCard } from '@/components/ui/ActionCard';

export function Settings() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { preferences, updatePreferences, isUpdating } =
    useNotificationPreferences();
  const [testState, setTestState] = useState<TestNotificationState>('idle');
  const [countdown, setCountdown] = useState(5);
  const [clearingCache, setClearingCache] = useState(false);
  const [cacheClearSuccess, setCacheClearSuccess] = useState(false);
  const [cacheClearError, setCacheClearError] = useState<string | null>(null);

  const { data: pushEnabled, isLoading: checkingPermission } = useQuery({
    queryKey: ['pushNotificationStatus'],
    queryFn: isPushEnabled,
    staleTime: 30 * 1000,
  });

  // Handle enabling push notifications
  const handleEnablePush = async () => {
    const granted = await requestNotificationPermission();
    queryClient.setQueryData(['pushNotificationStatus'], granted);
  };

  // Handle toggling a notification preference
  const handleTogglePreference = async (
    key:
      | 'notif_event_reminders'
      | 'notif_event_changes'
      | 'notif_community_comments'
  ) => {
    try {
      await updatePreferences({
        [key]: !preferences[key],
      });
    } catch (error) {
      console.error('Failed to update preference:', error);
    }
  };

  // Handle test notification with countdown
  const handleSendTestNotification = useCallback(async () => {
    if (testState !== 'idle') return;

    setTestState('countdown');
    setCountdown(5);

    // Countdown timer (visual only - actual delay is server-side)
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        clearInterval(countdownInterval);
        setTestState('error');
        return;
      }

      setTestState('sending');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-test-notification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ delay_seconds: 5 }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send notification');
      }

      setTestState('sent');
      setTimeout(() => setTestState('idle'), 3000);
    } catch (error) {
      console.error('Failed to send test notification:', error);
      clearInterval(countdownInterval);
      setTestState('error');
      setTimeout(() => setTestState('idle'), 3000);
    }
  }, [testState]);

  // Handle clearing cache
  const handleClearCache = async () => {
    setClearingCache(true);
    setCacheClearSuccess(false);
    setCacheClearError(null);

    try {
      // Clear Service Worker caches and browser storage
      await clearAllCaches();

      // Clear React Query cache
      queryClient.clear();

      setCacheClearSuccess(true);
      // Reset success message after 3 seconds
      setTimeout(() => setCacheClearSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to clear cache:', error);
      setCacheClearError(
        error instanceof Error
          ? error.message
          : SettingsContent.storage.clearCache.error
      );
      // Reset error message after 5 seconds
      setTimeout(() => setCacheClearError(null), 5000);
    } finally {
      setClearingCache(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-4 pb-8">
      <Header enableBack backTo="/account" />
      <h1 className="font-field-74 text-h1 text-slate-50 mb-6">
        {SettingsContent.title}
      </h1>

      <ActionCard
        hasBadge
        className="mb-4"
        title="Edit Account"
        icon={PencilIcon}
        badgeColor="purple"
        onClickAction={() => navigate({ to: '/account/edit' })}
      />

      <NotificationSettings
        pushEnabled={pushEnabled}
        checkingPermission={checkingPermission}
        preferences={preferences}
        isUpdating={isUpdating}
        testState={testState}
        countdown={countdown}
        onEnablePush={handleEnablePush}
        onTogglePreference={handleTogglePreference}
        onSendTestNotification={handleSendTestNotification}
      />

      {/* Storage Section */}
      <section className="bg-slate-700 rounded-2xl my-12 ">
        <div className="flex items-center gap-3 mb-2">
          <TrashIcon size={24} weight="regular" className="text-slate-400" />
          <h2 className="font-field-54 text-h2 text-purple-300">
            {SettingsContent.storage.title}
          </h2>
        </div>
        <p className="text-body text-slate-300 mb-6">
          {SettingsContent.storage.description}
        </p>

        <div className="space-y-4">
          <div>
            <p className="text-small text-slate-400 mb-3">
              {SettingsContent.storage.clearCache.description}
            </p>
            <Button
              onClick={handleClearCache}
              disabled={clearingCache}
              size="lg"
              variant="outline"
              className="w-full sm:w-auto"
            >
              {clearingCache
                ? SettingsContent.storage.clearCache.buttonClearing
                : SettingsContent.storage.clearCache.button}
            </Button>
          </div>

          {cacheClearSuccess && (
            <Banner
              variant="success"
              description={SettingsContent.storage.clearCache.success}
            />
          )}

          {cacheClearError && (
            <Banner variant="error" description={cacheClearError} />
          )}
        </div>
      </section>
    </div>
  );
}
